import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Linkedin,
  Pause,
  Plus,
  Radar,
  Send,
  ShieldCheck,
  Users,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { KpiCard } from '@/components/ui/KpiCard';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { ROUTES } from '@/lib/routes';
import { useOutreachAgents, useOutreachLeads, type OutreachAgent } from './api';
import { OutreachAgentFormDialog } from './OutreachAgentFormDialog';
import { linkedInMeta, leadStatusMeta, statusMeta } from './mockData';

function primaryCampaign(agent: OutreachAgent): string {
  return agent.assigned_campaigns[0]?.name ?? 'Non assignee';
}

export default function OutreachPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const wsId = currentWorkspace?.id;
  const [showCreate, setShowCreate] = useState(false);
  const agentsQuery = useOutreachAgents(wsId);
  const leadsQuery = useOutreachLeads(wsId);
  const agents = agentsQuery.data?.items ?? [];
  const leads = leadsQuery.data?.items ?? [];

  const activeAgents = agents.filter((agent) => agent.status === 'active').length;
  const attentionAgents = agents.filter(
    (agent) => agent.status === 'attention' || agent.linkedin_status === 'challenge',
  ).length;
  const connectedSessions = agents.filter((agent) => agent.linkedin_status === 'connected').length;
  const qualifiedToday = agents.reduce((total, agent) => total + agent.qualified_today, 0);
  const acceptedLeads = leads.filter((lead) => ['accepted', 'followed_up'].includes(lead.status)).length;
  const activityData = useMemo(
    () =>
      agents.map((agent) => ({
        name: agent.name,
        qualified: agent.qualified_today,
        invites: agent.daily_used,
      })),
    [agents],
  );

  if (!wsId) {
    return <div className="p-8 text-gray-500">Selectionne un workspace pour ouvrir Outreach.</div>;
  }

  return (
    <div>
      <PageHeader
        title="Outreach"
        description="Agents LinkedIn OpenOutreach connectes au Dispatcher"
        action={
          <div className="flex items-center gap-2">
            <Link to={ROUTES.OUTREACH_LEADS}>
              <Button variant="secondary">
                <Users size={14} /> Leads
              </Button>
            </Link>
            <Button onClick={() => setShowCreate(true)}>
              <Plus size={14} /> Creer un agent
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Agents actifs" value={`${activeAgents}/${agents.length}`} icon={Linkedin} color="bg-brand-primary" />
        <KpiCard label="Sessions LinkedIn" value={`${connectedSessions}/${agents.length}`} icon={ShieldCheck} color="bg-[#14261E]" />
        <KpiCard label="Profils qualifies" value={qualifiedToday} icon={Radar} color="bg-blue-500" sub="Aujourd'hui" />
        <KpiCard label="Leads chauds" value={acceptedLeads} icon={CheckCircle2} color="bg-emerald-500" sub="Acceptes ou relances" />
        <KpiCard label="A traiter" value={attentionAgents} icon={AlertTriangle} color="bg-red-500" sub="Challenge ou erreur" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2">
          <CardHeader
            title="Activite agents"
            subtitle="Profils qualifies et invitations consommees aujourd'hui"
            action={<Badge tone="brand">live</Badge>}
          />
          <CardContent>
            {activityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={activityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="qualified" name="Qualifies" fill="#55b88b" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="invites" name="Invitations" fill="#14261E" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState title="Aucun agent actif" description="Cree un agent pour voir les volumes ici." />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Attention" subtitle="Sessions a reprendre humainement" />
          <CardContent className="space-y-3">
            {agents
              .filter((agent) => agent.status === 'attention' || agent.linkedin_status === 'challenge')
              .map((agent) => {
                const meta = statusMeta[agent.status];
                return (
                  <Link
                    key={agent.id}
                    to={ROUTES.OUTREACH_AGENT_DETAIL(agent.id)}
                    className="flex items-center justify-between rounded-brand border border-brand-border px-3 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <meta.icon size={14} className="text-red-500 shrink-0" />
                        <span className="text-sm font-semibold text-brand-dark">{agent.name}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {agent.owner_name ?? 'A attribuer'} - {agent.linkedin_status}
                      </p>
                    </div>
                    <ArrowRight size={14} className="text-gray-400" />
                  </Link>
                );
              })}
            {attentionAgents === 0 && (
              <EmptyState title="Rien a traiter" description="Les agents n'ont pas de challenge signale." />
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-3">
          <CardHeader
            title="Agents"
            subtitle="Pool OpenOutreach relie au Dispatcher"
            action={
              <Link to={ROUTES.OUTREACH_AGENTS} className="text-xs font-medium text-brand-primary hover:underline">
                Voir tout
              </Link>
            }
          />
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-brand-border">
                  <th className="th">Agent</th>
                  <th className="th">LinkedIn</th>
                  <th className="th">Proxy</th>
                  <th className="th">Campagne</th>
                  <th className="th">Quota</th>
                  <th className="th">Qualifies</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => {
                  const status = statusMeta[agent.status];
                  const linkedIn = linkedInMeta[agent.linkedin_status];
                  return (
                    <tr key={agent.id} className="border-b border-brand-border table-row-hover">
                      <td className="td">
                        <Link to={ROUTES.OUTREACH_AGENT_DETAIL(agent.id)} className="font-semibold text-brand-dark hover:underline">
                          {agent.name}
                        </Link>
                        <div className="text-xs text-gray-400">{agent.owner_name ?? 'A attribuer'}</div>
                      </td>
                      <td className="td">
                        <div className="flex items-center gap-2">
                          <Badge tone={status.tone}>{status.label}</Badge>
                          <Badge tone={linkedIn.tone}>{linkedIn.label}</Badge>
                        </div>
                      </td>
                      <td className="td">{agent.proxy_country ?? '-'}</td>
                      <td className="td max-w-[220px] truncate">{primaryCampaign(agent)}</td>
                      <td className="td">{agent.daily_used}/{agent.daily_limit}</td>
                      <td className="td">{agent.qualified_today}</td>
                      <td className="td">
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="secondary" disabled={!agent.novnc_url}>
                            <Send size={12} /> Connecter
                          </Button>
                          <Button size="sm" variant="ghost" disabled>
                            <Pause size={12} /> Pause
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!agentsQuery.isLoading && agents.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState title="Aucun agent" description="Cree un agent pour demarrer le pool." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="xl:col-span-3">
          <CardHeader
            title="Derniers leads"
            subtitle="Pipeline LinkedIn synchronise"
            action={
              <Link to={ROUTES.OUTREACH_LEADS} className="text-xs font-medium text-brand-primary hover:underline">
                Ouvrir les leads
              </Link>
            }
          />
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-brand-border">
                  <th className="th">Lead</th>
                  <th className="th">Entreprise</th>
                  <th className="th">Agent</th>
                  <th className="th">Score</th>
                  <th className="th">Statut</th>
                  <th className="th">Dernier evenement</th>
                </tr>
              </thead>
              <tbody>
                {leads.slice(0, 4).map((lead) => {
                  const status = leadStatusMeta[lead.status];
                  return (
                    <tr key={lead.id} className="border-b border-brand-border table-row-hover">
                      <td className="td">
                        <div className="font-semibold text-brand-dark">{lead.full_name}</div>
                        <div className="text-xs text-gray-400">{lead.role_title ?? '-'}</div>
                      </td>
                      <td className="td">{lead.company ?? '-'}</td>
                      <td className="td">{lead.agent_name ?? '-'}</td>
                      <td className="td">{lead.score}</td>
                      <td className="td"><Badge tone={status.tone}>{status.label}</Badge></td>
                      <td className="td">{lead.last_event ?? '-'}</td>
                    </tr>
                  );
                })}
                {!leadsQuery.isLoading && leads.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState title="Aucun lead Outreach" description="Les leads apparaitront apres sync OpenOutreach." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <OutreachAgentFormDialog open={showCreate} onClose={() => setShowCreate(false)} workspaceId={wsId} />
    </div>
  );
}
